import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Avatar, AvatarImage } from "design-system/atoms/Avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Check } from "lucide-react"
import { CampaignTriggerIcon } from "modules/campaigns/components/CampaignTriggerIcon"
import { CampaignStatusBadge } from "modules/campaigns/components/Detail/CampaignStatusBadge"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import Locale from "modules/shared/components/Miscelanea/Locale"
import { ReactElement, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignHealthResultsResponseDataSchema } from "schema"
import { z } from "zod"

type Row = z.infer<typeof CampaignHealthResultsResponseDataSchema>[number]

const columnHelper = createColumnHelper<Features, Row>()

const SIGNIFICANT_DROP_RATIO = 0.35 // 35% drop vs the average of 7d+30d
const MIN_REFERENCE_CONVERSATIONS = 10
const MIN_REFERENCE_ENGAGEMENT_RATE = 5 // percentage points, since rates are already [0..100]

function isSignificantDrop(
  current: number,
  referenceA: number,
  referenceB: number,
  minReference: number,
) {
  const referenceAvg = (referenceA + referenceB) / 2
  if (referenceAvg < minReference) return false
  if (referenceAvg <= 0) return false
  if (current >= referenceAvg) return false
  return (referenceAvg - current) / referenceAvg >= SIGNIFICANT_DROP_RATIO
}

function getDropPercent(current: number, referenceA: number, referenceB: number) {
  const referenceAvg = (referenceA + referenceB) / 2
  if (referenceAvg <= 0) return 0
  const drop = ((referenceAvg - current) / referenceAvg) * 100
  return drop > 0 ? drop : 0
}

function renderWhatsappCount(value: number, show: boolean) {
  return <span className="font-mono">{show ? value : "-"}</span>
}

function renderWhatsappTemplateCount(value: number, show: boolean) {
  if (!show) return <span className="font-mono">{"-"}</span>

  let highlight: string | undefined
  if (value === 0) {
    highlight = "inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800"
  } else if (value === 1) {
    highlight = "inline-flex rounded px-2 py-0.5 bg-amber-50 text-amber-800"
  }

  return <span className={cn("font-mono", highlight)}>{value}</span>
}

function wrapWithTooltip(content: ReactElement, tooltipText: string) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  )
}

function renderFalsePill() {
  return <span className="inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800">{"false"}</span>
}

type Props = {
  data: z.infer<typeof CampaignHealthResultsResponseDataSchema> | undefined
  isPending: boolean
  showSearchPrompt?: boolean
  searchPromptMessage?: string
}

export const BackofficeCampaignHealthTable = ({
  data,
  isPending,
  showSearchPrompt,
  searchPromptMessage,
}: Props) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("backoffice.campaigns.health.table.name"),
          cell: (props) => {
            const name = props.getValue()

            return (
              <span className={cn(!name && "text-muted-foreground")}>
                {name ?? t("backoffice.campaigns.health.untitled")}
              </span>
            )
          },
        }),
        columnHelper.display({
          id: "company",
          header: t("backoffice.campaigns.health.table.companyName"),
          cell: ({ row }) => {
            const { companyName, companyLogo, organizationName } = row.original
            const displayName =
              companyName === organizationName
                ? companyName
                : `${companyName} (${organizationName})`

            return (
              <div className="flex items-center gap-2">
                <Avatar>
                  <AvatarImage src={companyLogo ?? undefined} alt={companyName ?? undefined} />
                </Avatar>
                <span>{displayName}</span>
              </div>
            )
          },
        }),
        columnHelper.accessor("organizationType", {
          header: t("backoffice.campaigns.health.table.organizationType"),
        }),
        columnHelper.accessor("lastLogin", {
          header: t("backoffice.campaigns.health.table.lastLogin"),
          cell: ({ getValue }) => {
            const v = getValue()
            return v ? <DateDisplay date={v} /> : "—"
          },
        }),
        columnHelper.accessor("hasBriefing", {
          header: t("backoffice.campaigns.health.table.hasBriefing"),
          cell: ({ getValue }) => {
            const value = getValue()
            if (!value) {
              return wrapWithTooltip(
                <div className="flex justify-center">{renderFalsePill()}</div>,
                t("backoffice.campaigns.health.tooltips.briefingMissing"),
              )
            }

            return <div className="flex justify-center">{"√"}</div>
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("status", {
          header: t("backoffice.campaigns.health.table.status"),
          cell: (props) => <CampaignStatusBadge status={props.getValue()} />,
        }),
        columnHelper.accessor("trigger", {
          header: t("backoffice.campaigns.health.table.trigger"),
          cell: (props) => <CampaignTriggerIcon trigger={props.getValue()} emptyValue="-" />,
        }),
        columnHelper.accessor("channel", {
          header: t("backoffice.campaigns.health.table.channel"),
          cell: (props) => <ChannelIcon channel={props.getValue()} withTooltip />,
        }),
        columnHelper.accessor("defaultLanguage", {
          header: t("backoffice.campaigns.health.table.defaultLanguage"),
          cell: (props) => {
            const value = props.getValue()
            if (!value) {
              return wrapWithTooltip(
                renderFalsePill(),
                t("backoffice.campaigns.health.tooltips.defaultLanguageMissing"),
              )
            }

            return <Locale code={value} />
          },
        }),
        columnHelper.accessor("ticketEscalation", {
          header: t("backoffice.campaigns.health.table.ticketEscalation"),
          cell: ({ getValue }) => {
            const value = getValue()
            if (!value) {
              return wrapWithTooltip(
                <div className="flex justify-center">{renderFalsePill()}</div>,
                t("backoffice.campaigns.health.tooltips.capabilitiesMissing"),
              )
            }

            return (
              <div className="flex justify-center">
                <Check className="h-4 w-4" />
              </div>
            )
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("bizOpEscalation", {
          header: t("backoffice.campaigns.health.table.bizOpEscalation"),
          cell: ({ getValue }) => {
            const value = getValue()
            if (!value) {
              return wrapWithTooltip(
                <div className="flex justify-center">{renderFalsePill()}</div>,
                t("backoffice.campaigns.health.tooltips.capabilitiesMissing"),
              )
            }

            return (
              <div className="flex justify-center">
                <Check className="h-4 w-4" />
              </div>
            )
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("reviews", {
          header: t("backoffice.campaigns.health.table.reviews"),
          cell: ({ getValue }) => {
            const value = getValue() ?? "false"
            const isFalse = value === "false"

            const content = (
              <span
                className={cn(isFalse && "inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800")}
              >
                {value}
              </span>
            )

            if (!isFalse) return content

            return wrapWithTooltip(
              content,
              t("backoffice.campaigns.health.tooltips.capabilitiesMissing"),
            )
          },
        }),
        columnHelper.accessor("avgAssistant", {
          header: t("backoffice.campaigns.health.table.avgAssistant"),
          cell: ({ getValue }) => <span className="font-mono">{getValue().toFixed(2)}</span>,
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("conversations30d", {
          header: t("backoffice.campaigns.health.table.conversations30d"),
          cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("conversations7d", {
          header: t("backoffice.campaigns.health.table.conversations7d"),
          cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("conversations3d", {
          header: t("backoffice.campaigns.health.table.conversations3d"),
          cell: ({ row, getValue }) => {
            const current = getValue()
            const dropPct = getDropPercent(
              current,
              row.original.conversations7d,
              row.original.conversations30d,
            )
            const highlight = isSignificantDrop(
              current,
              row.original.conversations7d,
              row.original.conversations30d,
              MIN_REFERENCE_CONVERSATIONS,
            )

            const tooltipText = t("backoffice.campaigns.health.tooltips.dropVsAvg", {
              pct: dropPct.toFixed(0),
            })

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "font-mono",
                      highlight && "inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800",
                    )}
                  >
                    {current}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("engagementRate30d", {
          header: t("backoffice.campaigns.health.table.engagementRate30d"),
          cell: ({ getValue }) => (
            <span className="font-mono">
              {getValue()}
              {"%"}
            </span>
          ),
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("engagementRate7d", {
          header: t("backoffice.campaigns.health.table.engagementRate7d"),
          cell: ({ getValue }) => (
            <span className="font-mono">
              {getValue()}
              {"%"}
            </span>
          ),
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("engagementRate3d", {
          header: t("backoffice.campaigns.health.table.engagementRate3d"),
          cell: ({ row, getValue }) => {
            const current = getValue()
            const dropPct = getDropPercent(
              current,
              row.original.engagementRate7d,
              row.original.engagementRate30d,
            )
            const highlight = isSignificantDrop(
              current,
              row.original.engagementRate7d,
              row.original.engagementRate30d,
              MIN_REFERENCE_ENGAGEMENT_RATE,
            )

            const tooltipText = t("backoffice.campaigns.health.tooltips.dropVsAvg", {
              pct: dropPct.toFixed(0),
            })

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "font-mono",
                      highlight && "inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800",
                    )}
                  >
                    {current}
                    {"%"}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("filterOut7d", {
          header: t("backoffice.campaigns.health.table.filterOut7d"),
          cell: ({ getValue }) => {
            const value = getValue()
            const content = <span className="font-mono">{value}</span>
            if (value === 0) return content
            return wrapWithTooltip(
              <span className="inline-flex rounded px-2 py-0.5 bg-red-50 text-red-800 font-mono">
                {value}
              </span>,
              t("backoffice.campaigns.health.tooltips.filterOut7dReview"),
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappTemplatesEs", {
          header: t("backoffice.campaigns.health.table.whatsappTemplatesEs"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger === "Webhook"
            const value = getValue()
            const content = renderWhatsappTemplateCount(value, show)
            if (!show || value >= 2) return content
            return wrapWithTooltip(
              content,
              t("backoffice.campaigns.health.tooltips.templatesLowRotation"),
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappTemplatesEn", {
          header: t("backoffice.campaigns.health.table.whatsappTemplatesEn"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger === "Webhook"
            const value = getValue()
            const content = renderWhatsappTemplateCount(value, show)
            if (!show || value >= 2) return content
            return wrapWithTooltip(
              content,
              t("backoffice.campaigns.health.tooltips.templatesLowRotation"),
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappTemplatesCat", {
          header: t("backoffice.campaigns.health.table.whatsappTemplatesCat"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger === "Webhook"
            const value = getValue()
            const content = renderWhatsappTemplateCount(value, show)
            if (!show || value >= 2) return content
            return wrapWithTooltip(
              content,
              t("backoffice.campaigns.health.tooltips.templatesLowRotation"),
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappTemplatesFr", {
          header: t("backoffice.campaigns.health.table.whatsappTemplatesFr"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger === "Webhook"
            const value = getValue()
            const content = renderWhatsappTemplateCount(value, show)
            if (!show || value >= 2) return content
            return wrapWithTooltip(
              content,
              t("backoffice.campaigns.health.tooltips.templatesLowRotation"),
            )
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappOpeningMessagesEs", {
          header: t("backoffice.campaigns.health.table.whatsappOpeningMessagesEs"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger !== "Webhook"
            return renderWhatsappCount(getValue(), show)
          },
          meta: { align: "right" as const },
        }),
        columnHelper.accessor("whatsappOpeningMessagesEn", {
          header: t("backoffice.campaigns.health.table.whatsappOpeningMessagesEn"),
          cell: ({ row, getValue }) => {
            const show = row.original.channel === "Whatsapp" && row.original.trigger !== "Webhook"
            return renderWhatsappCount(getValue(), show)
          },
          meta: { align: "right" as const },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  const totalColumns = useMemo(
    () => columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0),
    [columns],
  )

  function renderContent() {
    if (showSearchPrompt) {
      return (
        <TableEmptySlate
          colSpan={totalColumns}
          message={searchPromptMessage ?? t("backoffice.campaigns.health.searchPrompt")}
        />
      )
    }

    if (isPending || data === undefined) {
      return <TableSkeleton numRows={8} colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate colSpan={totalColumns} message={t("backoffice.campaigns.health.empty")} />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.campaignId}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
